/**
 * Trade quote application form.
 *
 * Submits via fetch to Shopify's native `contact` form endpoint (distinct
 * form id "TradeQuoteForm" + hidden "Enquiry Type" field so this is never
 * confused with a product quote request in the shared notification template).
 *
 * Klaviyo calls only fire if `window.klaviyo` exists. Marketing consent is
 * only ever passed to Klaviyo if the visitor explicitly checked that box -
 * the "Trade Applicant" property and the tracked event are recorded either
 * way, since those aren't marketing communication, just CRM/segmentation
 * data about who applied.
 */
class TradeQuoteForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.#handleSubmit(event));
  }

  // The form posts the normal way (no background fetch): Shopify's spam check rejects
  // background posts with a 400, which the old code ignored and showed "Thank you" anyway.
  // Shopify reloads the page with form.posted_successfully? or form.errors, rendered in the section.
  #handleSubmit(event) {
    if (!this.form.checkValidity()) {
      event.preventDefault();
      this.form.reportValidity();
      return;
    }

    const submittedAt = this.form.querySelector('[data-trade-field="submitted-at"]');
    if (submittedAt) submittedAt.value = new Date().toISOString();

    this.#trackKlaviyo(new FormData(this.form));
    // The button is left enabled: Shopify may show a spam check (hCaptcha) first, and a
    // visitor who closes it must be able to press Submit again.
  }

  #trackKlaviyo(formData) {
    if (typeof klaviyo === 'undefined') return;

    const email = formData.get('contact[email]');
    const phone = formData.get('contact[Phone Number]');
    const consented = formData.get('contact[Marketing Consent]') === 'Yes';

    const identifyProperties = {
      email: email || undefined,
      phone_number: phone || undefined,
      first_name: formData.get('contact[Contact Person]') || undefined,
      organization: formData.get('contact[Company Name]') || undefined,
      'Trade Applicant': true,
    };

    // Only ever signal email consent to Klaviyo if the visitor explicitly ticked the box.
    if (consented) {
      identifyProperties.$consent = ['email'];
    }

    try {
      klaviyo.identify(identifyProperties);

      klaviyo.track('Trade Quote Requested', {
        CompanyName: formData.get('contact[Company Name]'),
        ABN: formData.get('contact[ABN]'),
        CompanyType: formData.get('contact[Company Type]'),
        ProjectName: formData.get('contact[Project Name]'),
        ProjectType: formData.get('contact[Project Type]'),
        ProductsRequired: formData.get('contact[Products Required]'),
        Quantity: formData.get('contact[Quantity]'),
        ExpectedDeliveryDate: formData.get('contact[Expected Delivery Date]'),
        InstallationRequired: formData.get('contact[Installation Required]'),
        MarketingConsent: consented,
      });
    } catch {
      // Never block submission on a Klaviyo error.
    }
  }
}

if (!customElements.get('trade-quote-form')) customElements.define('trade-quote-form', TradeQuoteForm);
